import React, { useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { intl } from "LocaleWrapper";
import { arrayMoveImmutable } from "array-move";
import { useMutation, useQuery } from "@apollo/client";

import { Button, Card, showSnackbar, Tag, TagSize } from "@cofano/cck";
import { CardAction } from "@cofano/cck/Card/Card";

import { Alert, Box, SvgIconTypeMap, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import { DefaultComponentProps } from "@mui/material/OverridableComponent";
import AddIcon from "@mui/icons-material/Add";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import SyncProblemRoundedIcon from "@mui/icons-material/SyncProblemRounded";
import WrongLocationOutlinedIcon from "@mui/icons-material/WrongLocationOutlined";
import EditOffOutlinedIcon from "@mui/icons-material/EditOffOutlined";
import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import ShowerOutlinedIcon from "@mui/icons-material/ShowerOutlined";

import { usePermission } from "components/utils/Permission";

import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import CargoActionTypeTag from "view/cargos/components/tags/CargoActionTypeTag";

import { FormattedDate } from "utils/DateUtils";

import { CallReorderFragment } from "__generated__/view/voyages/graphql/voyage.fragments.generated";
import { ReorderCallsAndActionsDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";
import { GetVoyageReorderCallsDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { LocationType, ReorderErrorType } from "__generated__/enums";
import { GraphQLFormattedError } from "graphql/error";

import "./styling.scss";

type Call = CallReorderFragment;
type CargoAction = CallReorderFragment["cargos"][number];

type ValidationError = {
	callIds: number[],
	actionIds: number[],
	errorType: ReorderErrorType
};

type UpdatedIndex = {
	callIndex: number,
	actionIndex?: number
};

interface VoyagePlanCargoActionsProps {
	voyageId: number;
}

const VoyagePlanCargoActions = ({ voyageId }: VoyagePlanCargoActionsProps) => {
	const [ calls, setCalls ] = useState<CallReorderFragment[]>([]);
	const [ stableCalls, setStableCalls ] = useState<CallReorderFragment[]>([]);

	const [ updatedActionIndices, setUpdatedActionIndices ] = useState<UpdatedIndex[]>([]);
	const [ reorderCallsAndActionsMutation ] = useMutation(ReorderCallsAndActionsDocument);

	useQuery(GetVoyageReorderCallsDocument, {
		variables: {
			voyageId: voyageId,
		},
		onCompleted: (data) => {
			setCalls(data.voyage?.calls ?? []);
			setStableCalls(data.voyage?.calls ?? []);
		},
	});
	const [ errors, setErrors ] = useState<ValidationError[]>([]);

	const markUpdated = (callIndex: number, actionIndex: number | undefined = undefined) => {
		setUpdatedActionIndices(prev => [ ...prev, { callIndex, actionIndex } ]);
	};

	const applyReorder = (newCalls: Call[]) => {
		setCalls(newCalls);

		reorderCallsAndActionsMutation({
			variables: {
				voyageId: voyageId,
				calls: newCalls.map(c => {
					return {
						callId: c.id === 0 ? undefined : c.id,
						actionIds: c.cargos.map(cargo => cargo.id),
					};
				}),
			},
			context: {
				silentError: (graphQLErrors: GraphQLFormattedError[]) => {
					return graphQLErrors.some(x => x.extensions?.validationErrors);
				},
			},
		}).then(result => {
			const mutatedCalls = sortCallsAndActions(result.data?.reorderCallsAndActions || []);

			if (errors.length > 0) {
				showSnackbar(intl.formatMessage({ id: "REORDER_ERRORS_RESOLVED" }), { variant: "success" });
				setErrors([]);
			}

			setCalls(mutatedCalls);
			setStableCalls(mutatedCalls);
		}).catch((error) => {
			setErrors(error.graphQLErrors.flatMap((error: GraphQLFormattedError) =>
				error.extensions?.validationErrors ?? []) as ValidationError[]);
		}).finally(() => {
			setUpdatedActionIndices([]);
		});
	};

	const onDragEnd = ({ source, destination, type }: DropResult) => {

		// Make sure we have a valid destination
		if (!destination) {
			return;
		}

		if (type === "COLUMN") {
			reorderCalls(source.index, destination.index);
			return;
		}

		// Make sure we're actually moving the item
		if ((source.droppableId === destination.droppableId) && (destination.index === source.index)) {
			return;
		}

		const sourceCall = calls.find(c => c.id === Number(source.droppableId));
		const destinationCall = calls.find(c => c.id === Number(destination.droppableId));

		if (sourceCall && destination.droppableId.startsWith("new")) {
			moveCargoActionToNewCall(sourceCall, source.index, Number(destination.droppableId.split("new-")[1]));
		} else if (!sourceCall || !destinationCall) {
			return;
		} else if (sourceCall.id === destinationCall.id) {
			reorderActions(sourceCall, source.index, destination.index);
		} else {
			moveCargoActionToOtherCall(sourceCall, destinationCall, source.index, destination.index);
		}
	};

	const reorderCalls = (sourceIndex: number, destinationIndex: number) => {
		const newCalls = arrayMoveImmutable([ ...calls ], sourceIndex, destinationIndex);
		markUpdated(sourceIndex);
		markUpdated(destinationIndex);
		applyReorder(newCalls);
	};

	const reorderActions = (call: Call, sourceIndex: number, destinationIndex: number) => {
		const callIndex = calls.findIndex(c => c.id === call.id);
		markUpdated(callIndex, sourceIndex);
		markUpdated(callIndex, destinationIndex);

		const newCargoActionList = arrayMoveImmutable([ ...call.cargos ], sourceIndex, destinationIndex);
		const newCalls = [ ...calls ].map(c => ({
			...c,
			cargos: c.id === call.id ? newCargoActionList : c.cargos,
		}));

		applyReorder(newCalls);
	};

	const moveCargoActionToOtherCall = (sourceCall: Call, destinationCall: Call, sourceIndex: number, destinationIndex: number) => {
		const sourceCallIndex = calls.findIndex(c => c.id === sourceCall.id);
		const destinationCallIndex = calls.findIndex(c => c.id === destinationCall.id);
		markUpdated(sourceCallIndex, sourceIndex);
		markUpdated(destinationCallIndex, destinationIndex);

		const sourceCallCargoActions = sourceCall.cargos.filter((_, index: number) => index !== sourceIndex);

		const destinationCallCargoActions = [
			...destinationCall.cargos.slice(0, destinationIndex),
			sourceCall.cargos[sourceIndex],
			...destinationCall.cargos.slice(destinationIndex),
		];

		const newCalls = [ ...calls ].map(c => {
			if (c.id === sourceCall.id) {
				return {
					...c,
					cargos: sourceCallCargoActions,
				};
			} else if (c.id === destinationCall.id) {
				return {
					...c,
					cargos: destinationCallCargoActions,
				};
			} else {
				return c;
			}
		});

		applyReorder(newCalls);
	};

	const moveCargoActionToNewCall = (sourceCall: Call, sourceIndex: number, newCallIndex: number) => {
		const cargoAction = sourceCall.cargos[sourceIndex];
		if (!cargoAction.location) {
			return;
		}

		markUpdated(newCallIndex);

		const oldCalls = [ ...calls ].map(c => {
			if (c.id === sourceCall.id) {
				return {
					...c,
					cargos: c.cargos.filter((_, i) => i !== sourceIndex),
				};
			}
			return c;
		});

		const newCall: Call = {
			id: 0,
			location: cargoAction.location,
			pta: cargoAction.start,
			lastStatusChange: null,
			cargos: [ cargoAction ],
			simulatedTimestamp: cargoAction.simulatedTimestamp ?? false,
		};

		const newCalls = [
			...oldCalls.slice(0, newCallIndex),
			newCall,
			...oldCalls.slice(newCallIndex),
		];
		applyReorder(newCalls);
	};

	const revertChanges = () => {
		setCalls(stableCalls);
		setErrors([]);
	};

	const { isAdmin, isMainUserLimited, isCompanyUser, isReadOnly } = usePermission();
	const [ editMode, setEditMode ] = useState(false);
	const [ showSimulatedTimes, setShowSimulatedTimes ] = useState(false);
	const canEdit = editMode && (isAdmin || isMainUserLimited || isCompanyUser) && !isReadOnly;

	return (
		<Card
			disablePadding
			title={intl.formatMessage({ id: "CALL_PLANNING" })}
			icon={<DashboardCustomizeRoundedIcon />}
			actions={[
				{
					disabled: !canEdit,
					render: () => (
						<Box sx={{ alignItems: "center", display: "flex" }}>
							<Typography sx={{ fontSize: 13, fontWeight: 500 }} color="textPrimary">
								{intl.formatMessage({ id: "SIMULATED_TIMESTAMPS" })}
							</Typography>

							<Switch
								onChange={(checked) => setShowSimulatedTimes(checked.target.checked)}
								checked={showSimulatedTimes} />
						</Box>
					),
				},
				{
					disabled: !canEdit,
					render: () => (
						<Box sx={{ alignItems: "center", display: "flex" }}>
							<Typography sx={{ fontSize: 13, fontWeight: 500 }} color="textPrimary">
								{intl.formatMessage({ id: "CARGO_EDIT" })}
							</Typography>

							<Switch
								onChange={(checked) => setEditMode(checked.target.checked)}
								checked={editMode} />
						</Box>
					),
				}  ]}>

			{errors.length > 0 &&
				<Alert
					severity="error"
					action={<Button variant="text" disabled={stableCalls.length === 0} onClick={revertChanges}>{intl.formatMessage({ id: "REVERT" })}</Button>}>
					{intl.formatMessage({ id: "REORDER_ERRORS_CANNOT_SAVE" })}
				</Alert>
			}

			<div className="voyage-plan-cargo-actions">
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="board" type="COLUMN" direction="horizontal">
						{(provided, snapshot) => (
							<>
								<div className="board-create-call" ref={provided.innerRef} {...provided.droppableProps}>
									{calls.map((_call, index) => (
										<React.Fragment key={index}>
											{index === 0 && <CreateColumn droppableId="new-0" />}
											{calls[index + 1] && <CreateColumn droppableId={`new-${index + 1}`} />}
											{index + 1 === calls.length &&
											<CreateColumn droppableId={`new-${calls.length}`} />}
										</React.Fragment>
									))}
								</div>

								<div className={`board ${snapshot.isDraggingOver ? "dragging-over" : ""}`}
									ref={provided.innerRef}
									{...provided.droppableProps}>
									{calls.map((call, index) => (
										<React.Fragment key={call.id}>
											<CallColumn call={call}
												index={index}
												updatedActionIndices={updatedActionIndices}
												errors={errors}
												canEdit={canEdit}
												showSimulatedTimes={showSimulatedTimes}
											/>
										</React.Fragment>
									))}
									{provided.placeholder}
								</div>
							</>
						)}
					</Droppable>
				</DragDropContext>
			</div>
		</Card>
	);
};

type CreateColumnProps = { droppableId: string };
const CreateColumn = ({ droppableId }: CreateColumnProps) => {
	const theme = useTheme();
	return (
		<Droppable droppableId={droppableId} type={"CALL"}>
			{(provided, snapshot) => (
				<div
					className="create-column"
					style={{
						backgroundColor: snapshot.isDraggingOver ? "#bfd9bf" : (theme.palette.mode === "dark" ? theme.palette.background.default : "#F1F1F1"),
						border: theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.25)" : "1px solid #e1e1e1",
					}}
					ref={provided.innerRef}
					{...provided.droppableProps}>
					<div {...provided.droppableProps} ref={provided.innerRef}>
						<AddIcon fontSize="small" color="action" />
						{provided.placeholder}
					</div>
				</div>
			)}
		</Droppable>
	);
};

type ColumnListProps = {
	call: Call;
	index: number,
	updatedActionIndices: UpdatedIndex[],
	errors: ValidationError[],
	canEdit: boolean,
	showSimulatedTimes: boolean
};
const CallColumn = (
	{
		call,
		index,
		updatedActionIndices,
		errors,
		canEdit,
		showSimulatedTimes,
	}: ColumnListProps) => {
	const theme = useTheme();

	const isLoading = updatedActionIndices.some(it => it.callIndex === index);
	const callErrors = errors.filter(e => e.callIds.includes(call.id));

	return (
		<Draggable draggableId={call.id.toString()} index={index} isDragDisabled={!canEdit}>
			{(provided) => (
				<div className={`column ${isLoading ? "loading" : ""} ${!canEdit ? "disabled" : ""}`}
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
				>
					<Card
						title={call.location.description}
						subtitle={call.location.locationGroups?.find(x => x.type == LocationType.Port)?.description}
						disablePadding
						actions={errorsAsCardActions(callErrors)}>
						<Box sx={{ backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#e3e8ee", height: "100%", display: "flex", flexDirection: "column" }}>
							<Box sx={{ p: 2 }}>
								{call.lastStatusChange ? (
									<Tag text={call.lastStatusChange?.status?.description} color={call.lastStatusChange?.status.color} />
								) : (
									<Tag text={intl.formatMessage({ id: "DRAFT" })} color="#ccc" />
								)}
							</Box>

							{(showSimulatedTimes || !call.simulatedTimestamp) && (
								<Typography variant="h4" sx={{ fontSize: 12, fontWeight: 500, pl: 2, pr: 2, mb: 1 }} color="textPrimary">
									<FormattedDate date={call.pta} />
								</Typography>
							)}

							<ActionList call={call}
								index={index}
								updatedActionIndices={updatedActionIndices}
								errors={errors}
								canEdit={canEdit}
								showSimulatedTimes={showSimulatedTimes}
							/>
						</Box>
					</Card>
				</div>
			)}
		</Draggable>
	);
};

const ActionList = (
	{
		call,
		index,
		updatedActionIndices,
		errors,
		canEdit,
		showSimulatedTimes,
	}: ColumnListProps) => {
	return (
		<Droppable droppableId={call.id.toString()} type={"CALL"}>
			{(provided, snapshot) => (
				<div className={`list ${snapshot.isDraggingOver ? "dragging-over" : ""}`}
					ref={provided.innerRef}
					{...provided.droppableProps}>
					{call.cargos
						.sort((a, b) => (a.start ?? 0) - (b.end ?? 0))
						.map((cargoAction, actionIndex) => (
							<ActionCard
								cargoAction={cargoAction}
								callIndex={index}
								actionIndex={actionIndex}
								key={cargoAction.id}
								updatedActionIndices={updatedActionIndices}
								errors={errors}
								canEdit={canEdit}
								showSimulatedTimes={showSimulatedTimes}
							/>
						))}
					{provided.placeholder}
				</div>
			)}
		</Droppable>
	);
};

type ActionCardProps = {
	cargoAction: CargoAction;
	callIndex: number;
	actionIndex: number,
	updatedActionIndices: UpdatedIndex[],
	errors: ValidationError[],
	canEdit: boolean
	showSimulatedTimes: boolean
};

const ActionCard = ({
	cargoAction,
	callIndex,
	actionIndex,
	updatedActionIndices,
	errors,
	canEdit,
	showSimulatedTimes,
}: ActionCardProps) => {
	const isLoading = updatedActionIndices.some(it => it.callIndex === callIndex && it.actionIndex === actionIndex);
	const actionErrors = errors.filter(e => e.actionIds.includes(cargoAction.id));

	return (
		<Draggable draggableId={cargoAction.id.toString()} index={actionIndex} isDragDisabled={!canEdit}>
			{(provided) => (
				<div
					className={`item ${isLoading ? "loading" : ""}`}
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}>
					<Card
						title={<CargoActionTypeTag type={cargoAction.type} size={TagSize.SMALL} />}
						actions={[
							...errorsAsCardActions(actionErrors),
							{ render: () => <span style={{ marginLeft: 8 }}>{cargoAction.cargo.cargoNumber}</span> } ]}
					>
						<div>{cargoAction.cargo.product?.name}</div>
						<div>{cargoAction.reference}</div>
						{(showSimulatedTimes || !cargoAction.simulatedTimestamp) && (
							<CargoActionDate action={cargoAction} format="lll" />
						)}
					</Card>
				</div>
			)}
		</Draggable>
	);
};

const errorsAsCardActions = (errors: ValidationError[]): CardAction[] => {
	const errorMessages = new Set(errors.map(e => e.errorType)).values().toArray();

	const props: DefaultComponentProps<SvgIconTypeMap> = {
		color: "error",
		sx: { fontSize: 18 },
	};

	return errorMessages.map(error => {
		const errorIcon = {
			[ReorderErrorType.DischargingBeforeLoading]: <SyncProblemRoundedIcon { ...props } />,
			[ReorderErrorType.DischargingHasNoLoading]: <SyncProblemRoundedIcon { ...props } />,
			[ReorderErrorType.MismatchingLocation]: <WrongLocationOutlinedIcon { ...props } />,
			[ReorderErrorType.CannotEditCompletedCall]: <EditOffOutlinedIcon { ...props } />,
			[ReorderErrorType.BeforeCompletedCall]: <HistoryToggleOffOutlinedIcon { ...props } />,
			[ReorderErrorType.CleaningBeforeDischarging]: <CleaningServicesOutlinedIcon { ...props } />,
			[ReorderErrorType.PurgingAfterLoading]: <ShowerOutlinedIcon { ...props } />,
		};

		return {
			render: () => <Tooltip title={ intl.formatMessage({ id: `REORDER_ERROR_${error}` }) }>
				{errorIcon[error as keyof typeof errorIcon]}
			</Tooltip>,
		};
	});
};

const sortCallsAndActions = (calls: Call[]) => {
	return calls.sort((a, b) => (a.pta || 0) - (b.pta || 0)).map(
		(call) => ({
			...call,
			cargos: call!.cargos.sort((a, b) => (a.start || 0) - (b.end || 0)),
		})) as Call[];
};

export default VoyagePlanCargoActions;
